import { useEffect, useState } from "react";
import api, { formatApiError, getSignedFileUrl } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Receipt, Trash2, CheckCircle2 } from "lucide-react";

const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;

export default function PendingDocsPanel({ onCount }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ ledger: "ufficiale", tax_rate: 10, payment_method: "banca" });

  const load = async () => { const { data } = await api.get("/documents?status=pending"); setDocs(data); onCount && onCount(data.length); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const viewDocs = async (d) => {
    const paths = (d.receipt_paths && d.receipt_paths.length) ? d.receipt_paths : (d.receipt_path ? [d.receipt_path] : []);
    for (const p of paths) {
      try { window.open(await getSignedFileUrl(p), "_blank"); } catch { /* noop */ }
    }
  };
  const openSettle = (d) => { setSel(d); setForm({ ledger: "ufficiale", tax_rate: 10, payment_method: "banca" }); setOpen(true); };
  const settle = async () => {
    try {
      await api.post(`/documents/${sel.id}/settle`, { ...form, tax_rate: parseFloat(form.tax_rate) || 0 });
      toast.success("Documento registrato in contabilità"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare il documento?")) return; await api.delete(`/documents/${id}`); load(); };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Documenti caricati dalla Tracciabilità senza spesa registrata. Registrali in contabilità quando vuoi.</p>
      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="pending-docs-table">
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Fornitore</TableHead><TableHead>Articoli</TableHead><TableHead>Doc.</TableHead><TableHead className="text-right">Totale</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun documento da sistemare 🎉</TableCell></TableRow>}
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`pending-doc-${d.id}`}>
                <TableCell>{d.date}</TableCell>
                <TableCell className="font-medium">{d.supplier}</TableCell>
                <TableCell>{d.items_count || 0}</TableCell>
                <TableCell>{(d.receipt_path || (d.receipt_paths && d.receipt_paths.length)) ? <Button size="icon" variant="ghost" onClick={() => viewDocs(d)} data-testid={`view-doc-${d.id}`}><Receipt className="w-4 h-4 text-primary" /></Button> : "—"}</TableCell>
                <TableCell className="text-right font-medium">{fmt(d.total)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" className="rounded-full mr-1" onClick={() => openSettle(d)} data-testid={`settle-doc-${d.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Registra</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="settle-doc-dialog">
          <DialogHeader><DialogTitle>Registra spesa · {sel?.supplier}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Totale documento: <b>{fmt(sel?.total)}</b></p>
            <div className="space-y-2"><Label>Registro</Label>
              <Select value={form.ledger} onValueChange={(v) => setForm({ ...form, ledger: v })}>
                <SelectTrigger data-testid="settle-ledger"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ufficiale">Ufficiale (con IVA)</SelectItem><SelectItem value="black">Black</SelectItem></SelectContent>
              </Select>
            </div>
            {form.ledger === "ufficiale" && (
              <div className="space-y-2"><Label>IVA (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="settle-taxrate" /></div>
            )}
            <div className="space-y-2"><Label>Pagato da</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="settle-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cassa">Cassa locale</SelectItem><SelectItem value="contante">Portafoglio contanti</SelectItem><SelectItem value="sumup">SumUp</SelectItem><SelectItem value="banca">Banca</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={settle} className="rounded-full" data-testid="confirm-settle-button">Registra in contabilità</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
